/**
 * Probe outcome classifier (pure module): maps the outcome of the connect-time
 * `server/discover` probe onto one of four verdicts — modern era, the
 * spec-mandated `-32022` corrective continuation, legacy fallback (the plain
 * 2025 `initialize` handshake — on the probed connection for in-place probes,
 * on the session child's fresh pipe when the probe rode a disposable
 * sibling), or a typed connect error.
 *
 * The classifier is deliberately conservative: anything it does not positively
 * recognize as modern resolves to the legacy fallback, and a network outage or
 * an auth-status rejection (HTTP 401/403) is a typed connect error, never an
 * era verdict. The verdicts apply to the
 * negotiation phase only — an established modern connection is never silently
 * demoted to `initialize` by a later failure.
 */
import type { DiscoverResult } from '@modelcontextprotocol/core-internal';
import {
    codecForVersion,
    MODERN_WIRE_REVISION,
    modernProtocolVersions,
    SdkError,
    SdkErrorCode,
    SdkHttpError,
    UnsupportedProtocolVersionError
} from '@modelcontextprotocol/core-internal';

/**
 * The runtime environment the probe executed in. Only consulted for the
 * network-failure row: a browser CORS-preflight rejection is treated as a
 * legacy signal, while in Node a network failure stays a typed connect error.
 */
export type ProbeEnvironment = 'node' | 'browser';

/**
 * The transport class the probe ran on. Consulted for the timeout and closed
 * rows: a stdio probe that times out — or whose child exits, closing the
 * connection — signals a legacy server, while an HTTP timeout or close stays
 * a typed error. Anything that is not the stdio child-process transport is
 * treated like HTTP.
 */
export type ProbeTransportKind = 'stdio' | 'http';

/**
 * A normalized probe outcome, produced by the connect-time wiring from the raw
 * transport exchange.
 */
export type ProbeOutcome =
    | { kind: 'result'; result: unknown }
    /** Answered with a JSON-RPC error (any HTTP status, including 200-bodied errors and stdio in-band errors). */
    | { kind: 'rpc-error'; code: number; message: string; data?: unknown }
    /** The HTTP layer rejected the probe POST (non-2xx); `body` is the raw response text and `statusText` the HTTP reason phrase, when available. */
    | { kind: 'http-error'; status: number; body?: string; statusText?: string }
    | { kind: 'network-error'; error: unknown }
    /** The transport's auth flow challenged or failed during the probe send — an error stamped at a transport auth seam, or an `UnauthorizedError` (the foreign-transport contract). `error` propagates unchanged. */
    | { kind: 'auth-required'; error: Error }
    /** The transport reported close while the probe awaited its reply. */
    | { kind: 'closed' }
    /** No response arrived within the probe timeout. */
    | { kind: 'timeout'; timeoutMs: number };

export interface ProbeClassifierContext {
    /** Modern-era versions this client can negotiate, in preference order (never empty). */
    clientModernVersions: readonly string[];
    /** The version the probe carried in its `_meta` envelope (used to synthesize `data.requested` on typed errors). */
    requestedVersion: string;
    /**
     * Whether a legacy `initialize` fallback is possible — `false` for a
     * modern-only client and for `pin` mode. Without a fallback, rows carrying
     * modern evidence but no usable version overlap — a `DiscoverResult` with
     * no overlapping version, or a `-32022` whose `data.supported` lists only
     * legacy revisions — yield a typed `UnsupportedProtocolVersionError` built
     * from that evidence; the remaining rows that would have fallen back still
     * classify as `legacy`, and the caller reports them as a typed negotiation
     * error instead of starting an `initialize` handshake.
     */
    fallbackAvailable: boolean;
    /** See {@linkcode ProbeEnvironment}. */
    environment: ProbeEnvironment;
    /** See {@linkcode ProbeTransportKind}. */
    transportKind: ProbeTransportKind;
}

export type ProbeVerdict =
    /** Definitive modern evidence: select `version` and continue without `initialize`. */
    | { kind: 'modern'; version: string; discover: DiscoverResult }
    /**
     * `-32022` with a mutual modern version: re-send the probe at `version`.
     * Spec-mandated select-and-continue — the caller runs it exactly once and
     * arms a loop guard on the second rejection, throwing `error`.
     */
    | { kind: 'corrective'; version: string; error: UnsupportedProtocolVersionError }
    /** Definitive legacy signal or unrecognized shape: perform the plain legacy `initialize` handshake (on the probed connection in the in-place modes; on the session child's fresh pipe on the sibling path). */
    | { kind: 'legacy' }
    /** Typed connect error — never converted to an era verdict. */
    | { kind: 'error'; error: Error };

/**
 * A cached era verdict for `connect({ prior })` — the persistable subset of
 * the {@linkcode ProbeVerdict} vocabulary (`'modern'` = speaks 2026-07-28+,
 * `'legacy'` = 2025-era `initialize` only). Freshness is the supplying host's
 * responsibility: a stale modern verdict fails loudly at the first request,
 * but a stale legacy verdict succeeds silently forever (an upgraded server
 * still answers `initialize`) — date cached legacy verdicts in your own
 * storage and stop supplying them past your policy horizon. Full recipe:
 * the gateway guide (`docs/advanced/gateway.md`).
 */
export type PriorDiscovery =
    /** Adopt `discover` directly: zero round trips. */
    | { kind: 'modern'; discover: DiscoverResult }
    /** Known-legacy server: skip the `server/discover` probe and run the plain legacy `initialize` handshake. */
    | { kind: 'legacy' };

/** The `-32022` UnsupportedProtocolVersion protocol error code (negotiation-phase recognition). */
const UNSUPPORTED_PROTOCOL_VERSION = -32_022;
/**
 * Deliberately not probe-recognized in either direction: deployed servers
 * overload `-32001` (the SDK-conventional `Session not found` body on a 2025
 * stateful server), and the spec-assigned `-32020` (`HeaderMismatch`) /
 * `-32021` (`MissingRequiredClientCapability`) are not era evidence — all
 * fall into the conservative legacy default.
 */
const NOT_PROBE_RECOGNIZED = new Set([-32_001, -32_020, -32_021]);

/**
 * Classify a single probe outcome. Pure: no I/O, no state — loop-guard and
 * retry state live in the caller.
 */
export function classifyProbeOutcome(outcome: ProbeOutcome, context: ProbeClassifierContext): ProbeVerdict {
    switch (outcome.kind) {
        case 'result': {
            return classifyResult(outcome.result, context);
        }
        case 'rpc-error': {
            return classifyRpcError(outcome, context);
        }
        case 'http-error': {
            return classifyHttpError(outcome, context);
        }
        case 'network-error': {
            return classifyNetworkError(outcome.error, context);
        }
        case 'auth-required': {
            // Not era evidence: propagate the auth challenge unchanged so the
            // caller can run finishAuth() and reconnect — the reconnect probes
            // again with the token and settles the era with real evidence.
            // Converting to a legacy fallback here would re-run the auth flow
            // inside the same connect (a second authorization prompt) and then
            // handshake an auth-gated modern server as legacy.
            return { kind: 'error', error: outcome.error };
        }
        case 'closed': {
            if (context.transportKind === 'stdio') {
                // A stdio child that exits on the unrecognized probe instead of
                // answering is a legacy signal — the same backward-compatibility
                // rule as the timeout row below (SDKs like the official Rust one
                // terminate the server on ANY pre-initialize request).
                return { kind: 'legacy' };
            }
            // On HTTP a mid-probe close is an ambiguous network condition — the
            // same typed connect error as any probe transport failure.
            return classifyNetworkError(new Error('Connection closed during the version negotiation probe'), context);
        }
        case 'timeout': {
            if (context.transportKind === 'stdio') {
                // Per the stdio transport's backward-compatibility rule, a probe
                // nobody answers within the timeout indicates a legacy server —
                // fall back to `initialize` (the sibling path runs it on the
                // session child's fresh pipe; in-place probes reuse the stream).
                return { kind: 'legacy' };
            }
            // On HTTP a deployed server answers, so silence is an outage, not a
            // legacy signal: keep the typed timeout error (the compatibility
            // matrix keys the HTTP legacy signal to a 4xx, never to silence).
            return {
                kind: 'error',
                error: new SdkError(SdkErrorCode.RequestTimeout, `Version negotiation probe timed out after ${outcome.timeoutMs}ms`, {
                    timeout: outcome.timeoutMs
                })
            };
        }
    }
}

function classifyResult(result: unknown, context: ProbeClassifierContext): ProbeVerdict {
    // The 2026 wire schema carries the spec receiver-side leniency for
    // `resultType` ('complete'), `ttlMs` (0) and `cacheScope` ('private'), so
    // routing through the codec is behavior-neutral with the prior public-schema
    // parse for absent and malformed cache hints (`.catch()` per spec receiver
    // leniency): a server that omits or malforms them still classifies `modern`.
    const parsed = codecForVersion(MODERN_WIRE_REVISION).validateResult('server/discover', result);
    if (!parsed.ok) {
        // Unrecognized result shape: not modern evidence — conservative legacy fallback.
        return { kind: 'legacy' };
    }
    const supportedVersions = parsed.value.supportedVersions;
    const overlap = context.clientModernVersions.find(version => supportedVersions.includes(version));
    if (overlap !== undefined) {
        return { kind: 'modern', version: overlap, discover: parsed.value };
    }
    // A DiscoverResult with no overlap still drives era selection: the legacy
    // `initialize` fallback when available, otherwise a typed error.
    if (context.fallbackAvailable) {
        return { kind: 'legacy' };
    }
    return {
        kind: 'error',
        error: new UnsupportedProtocolVersionError({ supported: [...supportedVersions], requested: context.requestedVersion })
    };
}

function classifyRpcError(outcome: { code: number; message: string; data?: unknown }, context: ProbeClassifierContext): ProbeVerdict {
    const { code, message, data } = outcome;

    if (code === UNSUPPORTED_PROTOCOL_VERSION) {
        const supported = parseSupportedList(data);
        if (supported === undefined) {
            // -32022 without a valid data.supported list is not actionable modern evidence.
            return { kind: 'legacy' };
        }
        const requested = parseRequested(data) ?? context.requestedVersion;
        const error = new UnsupportedProtocolVersionError({ supported, requested }, message);
        const supportedModern = modernProtocolVersions(supported);
        const mutual = context.clientModernVersions.find(version => supportedModern.includes(version));
        if (mutual !== undefined) {
            // Mutual modern version: spec-mandated select-and-continue — never
            // fall back to initialize here.
            return { kind: 'corrective', version: mutual, error };
        }
        if (supportedModern.length > 0) {
            // Disjoint-but-modern list: typed error, never initialize.
            return { kind: 'error', error };
        }
        // Legacy-only list: definitive legacy signal (typed error for a modern-only client).
        return context.fallbackAvailable ? { kind: 'legacy' } : { kind: 'error', error };
    }

    if (NOT_PROBE_RECOGNIZED.has(code)) {
        return { kind: 'legacy' };
    }

    // Everything else — -32601, the deployed -32000 literals/free-text, code 0,
    // any unrecognized code — is a legacy signal or the conservative default.
    return { kind: 'legacy' };
}

function classifyHttpError(outcome: { status: number; body?: string; statusText?: string }, context: ProbeClassifierContext): ProbeVerdict {
    // Auth statuses are never era evidence, and a 401/403 body is the auth
    // layer's, not the server/discover handler's — this row ranks above the
    // JSON-RPC body parse. Parallel to the auth-required row: a typed failure
    // naming the status (response text on `data.text`), never a legacy
    // fallback. The codes are the auth ones, NOT EraNegotiationFailed: hosts
    // key era-recovery recipes (e.g. the gateway guide's cached-verdict flow)
    // on EraNegotiationFailed, and an auth wall must never enter era recovery
    // — persisting a legacy verdict for it would recreate the silent-legacy
    // bug at the fleet level.
    if (outcome.status === 401 || outcome.status === 403) {
        const isDenial = outcome.status === 403;
        return {
            kind: 'error',
            error: new SdkHttpError(
                isDenial ? SdkErrorCode.ClientHttpForbidden : SdkErrorCode.ClientHttpAuthentication,
                `Version negotiation failed: ${isDenial ? 'the server denied access (HTTP 403)' : 'the server requires authorization (HTTP 401)'}`,
                {
                    status: outcome.status,
                    statusText: outcome.statusText,
                    text: outcome.body
                }
            )
        };
    }
    if (outcome.status >= 500) {
        // A server failure is not era evidence, whatever the body says — this
        // row too ranks above the JSON-RPC body parse (a 5xx body is the
        // infrastructure's: a mid-deploy gateway's JSON error page, a crashed
        // handler's -32603, neither a server/discover verdict). The spec keys
        // the HTTP legacy signal to client-error rejections (a 2025 server
        // answers the unknown probe 4xx), never to 5xx.
        return {
            kind: 'error',
            error: new SdkHttpError(
                SdkErrorCode.EraNegotiationFailed,
                `Version negotiation failed: the server answered the probe with HTTP ${outcome.status}`,
                {
                    status: outcome.status,
                    statusText: outcome.statusText,
                    text: outcome.body
                }
            )
        };
    }
    // HTTP-rejected probes carry their JSON-RPC error in the response body — classify it like an in-band error.
    const rpcError = parseJsonRpcErrorBody(outcome.body);
    if (rpcError !== undefined) {
        return classifyRpcError(rpcError, context);
    }
    // Unparseable or unrecognized 4xx rejection: conservative legacy fallback.
    // With the auth and 5xx rows above, this row's legacy set now equals the
    // spec-licensed set exactly — the 4xx a deployed 2025 server answers a
    // request it does not recognize.
    return { kind: 'legacy' };
}

function classifyNetworkError(error: unknown, context: ProbeClassifierContext): ProbeVerdict {
    if (context.environment === 'browser' && isOpaqueFetchTypeError(error)) {
        // A browser CORS-preflight rejection against a deployed 2025 server is
        // an opaque TypeError. The legacy fallback also preflights (its JSON
        // Content-Type is non-simple), but carries only the pre-2026 headers a
        // deployed server's CORS allowlist already covers — so it can proceed
        // where the probe's 2026 headers could not.
        return { kind: 'legacy' };
    }
    return {
        kind: 'error',
        error: new SdkError(SdkErrorCode.EraNegotiationFailed, `Version negotiation probe failed: ${describeError(error)}`, {
            cause: error
        })
    };
}

function isOpaqueFetchTypeError(error: unknown): boolean {
    // Cross-realm safe: a bundled or sandboxed fetch may not share this realm's TypeError identity.
    return error instanceof TypeError || (error instanceof Error && error.name === 'TypeError');
}

function describeError(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

function parseSupportedList(data: unknown): string[] | undefined {
    if (typeof data !== 'object' || data === null) return undefined;
    const supported = (data as { supported?: unknown }).supported;
    if (!Array.isArray(supported) || supported.length === 0 || !supported.every(v => typeof v === 'string')) {
        return undefined;
    }
    return supported as string[];
}

function parseRequested(data: unknown): string | undefined {
    if (typeof data !== 'object' || data === null) return undefined;
    const requested = (data as { requested?: unknown }).requested;
    return typeof requested === 'string' ? requested : undefined;
}

function parseJsonRpcErrorBody(body: string | undefined): { code: number; message: string; data?: unknown } | undefined {
    if (body === undefined || body === '') return undefined;
    let parsed: unknown;
    try {
        parsed = JSON.parse(body);
    } catch {
        return undefined;
    }
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    const error = (parsed as { error?: unknown }).error;
    if (typeof error !== 'object' || error === null) return undefined;
    const { code, message, data } = error as { code?: unknown; message?: unknown; data?: unknown };
    if (typeof code !== 'number') return undefined;
    return { code, message: typeof message === 'string' ? message : '', data };
}
